import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Post } from "@/hooks/useSearch";
import { FavoriteButton } from "@/components/FavoriteButton";
import { PlaybackTimeline } from "@/components/PlaybackTimeline";
import { usePreferences } from "@/store/preferences";

interface Props {
  post: Post;
}

const RATING_COLORS: Record<string, string> = {
  s: "bg-green-700",
  q: "bg-yellow-700",
  e: "bg-red-800",
};

const ANIMATED_EXTS = new Set(["webm", "gif", "apng"]);

export function PostTile({ post }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { pausePreviews } = usePreferences();
  const [hovered, setHovered] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const isAnimated = ANIMATED_EXTS.has(post.file_ext);
  const isVideo = post.file_ext === "webm";
  const previewSrc = `/api/v1/media/preview/${post.id}`;
  const fullSrc = `/api/v1/media/file/${post.id}`;

  // Only play when actually hovered — auto-playing a screen full of videos
  // melts the browser and blasts bandwidth.
  const playing = hovered && isAnimated && !pausePreviews && !prefersReduced;

  useEffect(() => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.play().catch(() => undefined);
    } else {
      videoRef.current.pause();
    }
  }, [playing]);

  // Preview 404 → the backend has already flagged the post deleted (see
  // app/routes/media.py). On the current page we just disappear so the user
  // isn't staring at broken-image placeholders and the grid reflows.
  // Subsequent searches won't return this post at all.
  //
  // NB: this early return MUST be after every hook call above — moving it
  // earlier breaks rules-of-hooks (the next render would call fewer hooks)
  // and produces a hard React error that React Router presents as #300/#310.
  if (previewFailed) return null;

  const aspectRatio = post.width && post.height ? post.width / post.height : 1;

  return (
    <div className="relative">
      <Link
        to={`/posts/${post.id}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className="block relative overflow-hidden rounded-lg bg-surface-2 group focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <div style={{ paddingBottom: `${(1 / aspectRatio) * 100}%` }} className="relative">
          {/* Static JPG preview is always the base layer. */}
          <img
            src={previewSrc}
            alt={`Post ${post.id}`}
            loading="lazy"
            onError={() => setPreviewFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Animated overlay only mounts while hovered, so unhovering cancels
              the in-flight network request and the player. */}
          {playing && (isVideo ? (
            <video
              ref={videoRef}
              src={fullSrc}
              loop
              muted
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <img
              src={fullSrc}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ))}

          {/* Playback progress: exact for webm, approximated for gif/apng. */}
          {playing && (
            <PlaybackTimeline
              videoRef={isVideo ? videoRef : undefined}
              duration={isVideo ? null : post.duration}
              active={playing}
            />
          )}

          {/* Hint that the tile is animated. */}
          {isAnimated && !playing && (
            <div className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[10px] font-bold rounded px-1.5 py-0.5 uppercase tracking-wider">
              ▶ {post.file_ext}
            </div>
          )}

          {/* Heart toggle (logged-in only). Rendered via absolute positioning
              so it can sit over the preview without re-layout. */}
          <div className="absolute top-1.5 left-1.5">
            <FavoriteButton
              postId={post.id}
              compact
              className="bg-black/60 rounded-full px-1.5 py-0.5 text-base leading-none"
            />
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1.5 text-xs">
            <span
              className={`px-1.5 py-0.5 rounded text-white text-[10px] font-bold uppercase ${RATING_COLORS[post.rating] ?? "bg-zinc-700"}`}
            >
              {post.rating}
            </span>
            <span className="text-zinc-300 font-mono">{post.file_ext}</span>
            <span className="ml-auto text-zinc-300">▲ {post.score}</span>
          </div>
        </div>
      </Link>
    </div>
  );
}
